import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import { useToast } from "../../toast-provider";
import { useAppSelector } from "../../../redux/hooks";
import UserServices from "../../../services/UserServices";
import BotsServices, { IWhatsAppQrCodeBot } from "../../../services/bots";
import { v } from "../../../util";
import { MyCalendar } from "../../MyCalendar";
import useCalendar, {
  CalendarEditSharedUserItem,
  CalendarEditDayOffItem,
  CalendarEditWorkDayItem,
  SaveCalendarEditInput
} from "../Calendar/use-calendar";
import {
  AddPeriodButton,
  ConfirmationField,
  ConfirmationPreviewBox,
  ConfirmationSectionIcon,
  ConfirmationTextarea,
  ConfirmationVariableChip,
  ConfirmationVariablesLabel,
  ConfirmationVariablesRow,
  DayOffAction,
  DayOffAddButton,
  DayOffCalendarWrapper,
  DayOffSectionIcon,
  DayOffDateField,
  DayOffChip,
  DayOffChipRemoveButton,
  DayOffDescriptionField,
  DayOffFormCard,
  DayOffInput,
  DayOffLabel,
  DayOffList,
  DayOffSecondaryButton,
  DayOffSuggestionsHeader,
  DayOffSuggestionsTitle,
  DeletePeriodButton,
  EditCard,
  FullscreenLoading,
  MeetingGapDropdown,
  MeetingGapField,
  MeetingGapHint,
  MeetingGapSectionIcon,
  SaveActions,
  SaveButton,
  SettingsContent,
  SettingsHint,
  SettingsSection,
  SettingsSectionDescription,
  SettingsSectionHeader,
  SettingsSectionIcon,
  SettingsSectionTitle,
  SettingsSectionTitleGroup,
  ShareAgendaEmptyState,
  ShareAgendaField,
  ShareAgendaChip,
  ShareAgendaLabel,
  ShareAgendaMultiSelect,
  ShareAgendaSectionIcon,
  TimeDropdown,
  WorkDayCard,
  WorkDayHeader,
  WorkDayHeaderLeft,
  WorkDayPeriods,
  WorkDayTitle,
  WorkDayUnavailable,
  WorkPeriodLabel,
  WorkPeriodRow,
  WorkPeriodSeparator
} from "./styles";

type WorkWeekDay =
  | "SUNDAY"
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY";

type WorkPeriod = {
  id: number | string;
  persistedId: number | null;
  startTime: string;
  endTime: string;
};

type WorkDaySettings = {
  id: number | null;
  weekDay: WorkWeekDay;
  label: string;
  enabled: boolean;
  periods: WorkPeriod[];
};

type DayOffSuggestion = {
  id: string;
  month: number;
  day: number;
  description: string;
};

type DayOffItem = {
  id: number | string;
  persistedId: number | null;
  date: string;
  description: string;
  label: string;
};

type SharedUserOption = {
  id: number;
  name: string;
  email: string;
};

type MeetingGapOption = {
  label: string;
  value: number;
};

type BotOption = {
  label: string;
  value: number | "__none__";
};

type CalendarEditProps = {
  calendarId: number;
  companyId: number;
  canEditSettings?: boolean;
  canManageSharing?: boolean;
  onSaved?: () => Promise<void> | void;
  onDirtyChange?: (hasUnsavedChanges: boolean) => void;
};

type CalendarEditValidationForm = {
  name: string;
  confirmationMessage: string;
  breakAfterMinutes: number;
  workDays: {
    label: string;
    enabled: boolean;
    periods: {
      startTime: string;
      endTime: string;
    }[];
  }[];
  dayOffs: {
    date: string;
    description: string;
  }[];
};

const WORK_DAY_LABELS: Record<WorkWeekDay, string> = {
  SUNDAY: "Domingo",
  MONDAY: "Segunda-feira",
  TUESDAY: "Terça-feira",
  WEDNESDAY: "Quarta-feira",
  THURSDAY: "Quinta-feira",
  FRIDAY: "Sexta-feira",
  SATURDAY: "Sábado"
};

const WORK_DAY_ORDER: WorkWeekDay[] = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY"
];

const CONFIRMATION_PREVIEW_NAMES = [
  "João Silva",
  "Maria Oliveira",
  "Carlos Souza",
  "Ana Beatriz Costa",
  "Fernanda Martins"
];

const DEFAULT_WORK_PERIODS = [
  { id: "period-1", startTime: "08:00:00", endTime: "12:00:00" },
  { id: "period-2", startTime: "13:30:00", endTime: "18:00:00" }
];

const INITIAL_WORK_DAYS: WorkDaySettings[] = WORK_DAY_ORDER.map((weekDay) => ({
  id: null,
  weekDay,
  label: WORK_DAY_LABELS[weekDay],
  enabled: weekDay !== "SUNDAY" && weekDay !== "SATURDAY",
  periods:
    weekDay === "SUNDAY" || weekDay === "SATURDAY"
      ? []
      : DEFAULT_WORK_PERIODS.map((period) => ({
          ...period,
          persistedId: null,
          id: `${weekDay}-${period.id}`
        }))
}));

const NATIONAL_HOLIDAY_SUGGESTIONS: DayOffSuggestion[] = [
  {
    id: "holiday-2",
    month: 1,
    day: 1,
    description: "Confraternização Universal"
  },
  { id: "holiday-3", month: 4, day: 18, description: "Sexta-feira Santa" },
  { id: "holiday-4", month: 4, day: 21, description: "Tiradentes" },
  { id: "holiday-5", month: 5, day: 1, description: "Dia do Trabalho" },
  { id: "holiday-6", month: 6, day: 19, description: "Corpus Christi" },
  {
    id: "holiday-7",
    month: 9,
    day: 7,
    description: "Independência do Brasil"
  },
  {
    id: "holiday-8",
    month: 10,
    day: 12,
    description: "Nossa Senhora Aparecida"
  },
  { id: "holiday-9", month: 11, day: 2, description: "Finados" },
  {
    id: "holiday-10",
    month: 11,
    day: 15,
    description: "Proclamação da República"
  },
  {
    id: "holiday-11",
    month: 11,
    day: 20,
    description: "Consciência Negra"
  },
  { id: "holiday-12", month: 12, day: 24, description: "Véspera de Natal" },
  { id: "holiday-13", month: 12, day: 25, description: "Natal" },
  { id: "holiday-1", month: 12, day: 31, description: "Véspera de Ano Novo" }
];

const MEETING_GAP_OPTIONS: MeetingGapOption[] = [
  { label: "Sem intervalo", value: 0 },
  { label: "15 minutos", value: 15 },
  { label: "30 minutos", value: 30 },
  { label: "1 hora", value: 60 },
  { label: "2 horas", value: 120 }
];

const NO_BOT_OPTION: BotOption = {
  label: "Sem robô",
  value: "__none__"
};

const calendarEditSchema = v.object({
  name: v.string().superRefine(v.nonempty()),
  confirmationMessage: v.string().trim().superRefine(v.nonempty()),
  breakAfterMinutes: v
    .number()
    .refine(
      (value) => MEETING_GAP_OPTIONS.some((option) => option.value === value),
      "Escolha um intervalo"
    ),
  workDays: v.array(
    v
      .object({
        label: v.string(),
        enabled: v.boolean(),
        periods: v.array(
          v.object({
            startTime: v.string().superRefine(v.nonempty()),
            endTime: v.string().superRefine(v.nonempty())
          })
        )
      })
      .superRefine((workDay, ctx) => {
        if (workDay.enabled && workDay.periods.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: `${workDay.label}: adicione pelo menos um período`
          });
        }

        workDay.periods.forEach((period, index) => {
          if (period.startTime >= period.endTime) {
            ctx.addIssue({
              code: "custom",
              message: `${workDay.label}: o período ${
                index + 1
              } deve terminar após o início`
            });
          }
        });
      })
  ),
  dayOffs: v
    .array(
      v.object({
        date: v.string().superRefine(v.nonempty()),
        description: v.string().superRefine(v.nonempty())
      })
    )
    .superRefine((dayOffs, ctx) => {
      const seenDates = new Set<string>();

      dayOffs.forEach((dayOff) => {
        if (seenDates.has(dayOff.date)) {
          ctx.addIssue({
            code: "custom",
            message: `O dia sem atendimento ${dayOff.date} está duplicado`
          });
        }

        seenDates.add(dayOff.date);
      });
    })
});

const buildDayOffLabel = (date: Date, description: string) =>
  `${format(date, "dd/MM/yy")} - ${description}`;

const buildSuggestionDate = (suggestion: DayOffSuggestion) =>
  new Date(new Date().getFullYear(), suggestion.month - 1, suggestion.day);

const mapBackendWorkDays = (workDays: CalendarEditWorkDayItem[]) =>
  WORK_DAY_ORDER.map((weekDay) => {
    const workDay = workDays.find((item) => item.weekDay === weekDay);

    if (!workDay) {
      return {
        id: null,
        weekDay,
        label: WORK_DAY_LABELS[weekDay],
        enabled: false,
        periods: []
      };
    }

    return {
      id: workDay.id,
      weekDay,
      label: WORK_DAY_LABELS[weekDay],
      enabled: true,
      periods: workDay.periods.map((period) => ({
        id: period.id,
        persistedId: period.id,
        startTime: period.startTime,
        endTime: period.endTime
      }))
    };
  });

const mapBackendDayOffs = (dayOffs: CalendarEditDayOffItem[]) =>
  dayOffs.map((dayOff) => ({
    id: dayOff.id,
    persistedId: dayOff.id,
    date: dayOff.date,
    description: dayOff.description ?? "",
    label: buildDayOffLabel(
      new Date(`${dayOff.date}T00:00:00`),
      dayOff.description ?? ""
    )
  }));

const normalizeSharedUserIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is number => typeof item === "number");
};

const buildCalendarEditSnapshot = (input: {
  calendarName: string;
  meetingGapMinutes: number;
  confirmationMessage: string;
  botId: number | null;
  sharedUserIds: number[];
  workDaysSettings: WorkDaySettings[];
  selectedDayOffs: DayOffItem[];
}) =>
  JSON.stringify({
    calendarName: input.calendarName,
    meetingGapMinutes: input.meetingGapMinutes,
    confirmationMessage: input.confirmationMessage,
    botId: input.botId,
    sharedUserIds: [...input.sharedUserIds].sort((left, right) => left - right),
    workDaysSettings: input.workDaysSettings.map((workDay) => ({
      id: workDay.id,
      weekDay: workDay.weekDay,
      enabled: workDay.enabled,
      periods: workDay.periods.map((period) => ({
        persistedId: period.persistedId,
        startTime: period.startTime,
        endTime: period.endTime
      }))
    })),
    selectedDayOffs: [...input.selectedDayOffs]
      .map((dayOff) => ({
        persistedId: dayOff.persistedId,
        date: dayOff.date,
        description: dayOff.description
      }))
      .sort((left, right) =>
        `${left.date}-${left.description}`.localeCompare(
          `${right.date}-${right.description}`
        )
      )
  });

const getFirstErrorMessage = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "object" && value !== null && "message" in value) {
    const message = Reflect.get(value, "message");

    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = getFirstErrorMessage(item);

      if (message) {
        return message;
      }
    }
  }

  if (typeof value === "object" && value !== null) {
    for (const nestedValue of Object.values(value)) {
      const message = getFirstErrorMessage(nestedValue);

      if (message) {
        return message;
      }
    }
  }

  return null;
};

export const CalendarEdit: React.FC<CalendarEditProps> = ({
  calendarId,
  companyId,
  canEditSettings = true,
  canManageSharing = true,
  onSaved,
  onDirtyChange
}) => {
  const { user } = useAuth() as AuthContextType;
  const { getCalendarEdit, saveCalendarEdit } = useCalendar();
  const { getUser } = UserServices();
  const { GetBotsWhatsAppQrCode } = BotsServices();
  const toast = useToast();
  const selectedWorkspacesIds = useAppSelector(
    (state) => state.workspaces.selectedWorkspaces
  );
  const [calendarName, setCalendarName] = useState("");
  const [workDaysSettings, setWorkDaysSettings] =
    useState<WorkDaySettings[]>(INITIAL_WORK_DAYS);
  const [selectedDayOffs, setSelectedDayOffs] = useState<DayOffItem[]>([]);
  const [dayOffDate, setDayOffDate] = useState<Date | null>(null);
  const [dayOffDescription, setDayOffDescription] = useState("");
  const [ownerUserId, setOwnerUserId] = useState<number | null>(null);
  const [sharedUserIds, setSharedUserIds] = useState<number[]>([]);
  const [sharedUserOptions, setSharedUserOptions] = useState<
    SharedUserOption[]
  >([]);
  const [meetingGapMinutes, setMeetingGapMinutes] = useState<number>(15);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [selectedBotId, setSelectedBotId] = useState<number | null>(null);
  const [botOptions, setBotOptions] = useState<BotOption[]>([]);
  const [showRemoveBotModal, setShowRemoveBotModal] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const {
    reset,
    trigger,
    formState: { errors }
  } = useForm<CalendarEditValidationForm>({
    resolver: zodResolver(calendarEditSchema),
    defaultValues: {
      name: "",
      confirmationMessage: "",
      breakAfterMinutes: 15,
      workDays: [],
      dayOffs: []
    }
  });
  const [previewName] = useState<string>(
    () =>
      CONFIRMATION_PREVIEW_NAMES[
        Math.floor(Math.random() * CONFIRMATION_PREVIEW_NAMES.length)
      ]
  );
  const primaryColor = user.plataform.mainColor || "#2624CD";
  const isDayOffFormComplete = Boolean(
    dayOffDate && dayOffDescription.trim().length > 0
  );
  const canSaveChanges = canEditSettings || canManageSharing;
  const readOnlyCardStyle = {
    opacity: 0.6,
    pointerEvents: "none" as const
  };
  const availableHolidaySuggestions = useMemo(
    () =>
      NATIONAL_HOLIDAY_SUGGESTIONS.filter((suggestion) => {
        const suggestionDate = format(
          buildSuggestionDate(suggestion),
          "yyyy-MM-dd"
        );
        return !selectedDayOffs.some(
          (selectedItem) => selectedItem.date === suggestionDate
        );
      }),
    [selectedDayOffs]
  );
  const confirmationPreview = useMemo(() => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    const formattedDate = format(now, "dd/MM/yyyy");
    const formattedTime = format(oneHourLater, "HH:mm");

    return (confirmationMessage || "")
      .replaceAll("{{nome}}", previewName)
      .replaceAll("{{data}}", formattedDate)
      .replaceAll("{{horario}}", formattedTime)
      .replaceAll("{{hora}}", formattedTime);
  }, [confirmationMessage, previewName]);
  const currentSnapshot = useMemo(
    () =>
      buildCalendarEditSnapshot({
        calendarName,
        meetingGapMinutes,
        confirmationMessage,
        botId: selectedBotId,
        sharedUserIds,
        workDaysSettings,
        selectedDayOffs
      }),
    [
      calendarName,
      meetingGapMinutes,
      confirmationMessage,
      selectedBotId,
      sharedUserIds,
      workDaysSettings,
      selectedDayOffs
    ]
  );

  const timeOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];

    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 15) {
        const label = `${String(hour).padStart(2, "0")}:${String(
          minute
        ).padStart(2, "0")}`;

        options.push({
          label,
          value: `${label}:00`
        });
      }
    }

    return options;
  }, []);

  const botDropdownOptions = useMemo(() => {
    const hasSelectedBotInOptions =
      selectedBotId !== null &&
      botOptions.some((option) => option.value === selectedBotId);

    const connectedBotOptions =
      selectedBotId !== null && !hasSelectedBotInOptions
        ? [
            {
              label: "Robô selecionado (indisponível)",
              value: selectedBotId
            } satisfies BotOption,
            ...botOptions
          ]
        : botOptions;

    if (selectedBotId === null) {
      return [NO_BOT_OPTION, ...connectedBotOptions];
    }

    return [NO_BOT_OPTION, ...connectedBotOptions];
  }, [botOptions, selectedBotId]);

  const getSharedUserOption = (value: unknown) => {
    if (typeof value !== "number") {
      return null;
    }

    return sharedUserOptions.find((item) => item.id === value) ?? null;
  };

  useEffect(() => {
    let isMounted = true;

    const loadConnectedBots = async () => {
      const workspaceIds =
        selectedWorkspacesIds.length > 0
          ? selectedWorkspacesIds
          : user.workspaces?.workspaces?.map((workspace) => workspace.id) || [];

      if (!companyId || workspaceIds.length === 0) {
        if (isMounted) {
          setBotOptions([]);
        }
        return;
      }

      try {
        const response = await GetBotsWhatsAppQrCode({
          companyId,
          workspaces: workspaceIds
        });

        if (!isMounted) {
          return;
        }

        const nextBotOptions = (response ?? [])
          .map((bot: IWhatsAppQrCodeBot) => ({
            label: bot.name,
            value: bot.id
          }))
          .sort((left, right) => left.label.localeCompare(right.label));

        setBotOptions(nextBotOptions);
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        setBotOptions([]);

        const backendMessage =
          error?.message ||
          error?.response?.data?.message ||
          "Erro ao buscar robôs conectados.";

        toast?.current?.show({
          severity: "error",
          content: backendMessage
        });
      }
    };

    const loadCalendarEdit = async () => {
      const workspaceIds =
        selectedWorkspacesIds.length > 0
          ? selectedWorkspacesIds
          : user.workspaces?.workspaces?.map((workspace) => workspace.id) || [];

      setIsLoading(true);

      try {
        const calendarResponse = await getCalendarEdit(calendarId);
        const ownerUserId =
          calendarResponse.sharedUsers.find(
            (sharedUser: CalendarEditSharedUserItem) => sharedUser.owner
          )?.userId ?? null;

        let usersResponse: Awaited<ReturnType<typeof getUser>> = {
          result: [],
          total: 0
        };

        if (workspaceIds.length > 0) {
          try {
            usersResponse = await getUser({
              companyId,
              offset: 0,
              limit: 100,
              workspaceId: workspaceIds
            });
          } catch (_error) {
            usersResponse = { result: [], total: 0 };
          }
        }

        if (!isMounted) {
          return;
        }

        const nextCalendarName = calendarResponse.name;
        const nextConfirmationMessage =
          calendarResponse.confirmationMessage ?? "";
        const nextMeetingGapMinutes = calendarResponse.breakAfterMinutes ?? 15;
        const nextBotId = calendarResponse.botId ?? null;
        const nextSharedUserIds = calendarResponse.sharedUsers
          .map((sharedUser: CalendarEditSharedUserItem) => sharedUser.userId)
          .filter((userId) => userId !== ownerUserId);
        const nextWorkDaysSettings = mapBackendWorkDays(
          calendarResponse.workDays
        );
        const nextSelectedDayOffs = mapBackendDayOffs(calendarResponse.dayOffs);

        setCalendarName(nextCalendarName);
        setConfirmationMessage(nextConfirmationMessage);
        setMeetingGapMinutes(nextMeetingGapMinutes);
        setSelectedBotId(nextBotId);
        setOwnerUserId(ownerUserId);
        setSharedUserIds(nextSharedUserIds);
        setWorkDaysSettings(nextWorkDaysSettings);
        setSelectedDayOffs(nextSelectedDayOffs);
        setSharedUserOptions(
          usersResponse.result
            .map((userItem) => ({
              id: userItem.id,
              name: userItem.name,
              email: userItem.email
            }))
            .filter((userItem) => userItem.id !== ownerUserId)
            .sort((left, right) => left.name.localeCompare(right.name))
        );
        setInitialSnapshot(
          buildCalendarEditSnapshot({
            calendarName: nextCalendarName,
            meetingGapMinutes: nextMeetingGapMinutes,
            confirmationMessage: nextConfirmationMessage,
            botId: nextBotId,
            sharedUserIds: nextSharedUserIds,
            workDaysSettings: nextWorkDaysSettings,
            selectedDayOffs: nextSelectedDayOffs
          })
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadConnectedBots();
    loadCalendarEdit();

    return () => {
      onDirtyChange?.(false);
      isMounted = false;
    };
  }, [
    calendarId,
    companyId,
    getCalendarEdit,
    getUser,
    onDirtyChange,
    selectedWorkspacesIds,
    user.workspaces
  ]);

  useEffect(() => {
    if (isLoading || initialSnapshot === null) {
      return;
    }

    onDirtyChange?.(currentSnapshot !== initialSnapshot);
  }, [currentSnapshot, initialSnapshot, isLoading, onDirtyChange]);

  const toggleWorkDay = (weekDay: WorkWeekDay, enabled: boolean) => {
    setWorkDaysSettings((currentValue) =>
      currentValue.map((workDay) =>
        workDay.weekDay === weekDay
          ? {
              ...workDay,
              enabled,
              periods: enabled
                ? workDay.periods.length > 0
                  ? workDay.periods
                  : DEFAULT_WORK_PERIODS.map((period) => ({
                      ...period,
                      persistedId: null,
                      id: `${weekDay}-${period.id}-${Date.now()}`
                    }))
                : []
            }
          : workDay
      )
    );
  };

  const updateWorkPeriod = (
    weekDay: WorkWeekDay,
    periodId: number | string,
    field: "startTime" | "endTime",
    value: string
  ) => {
    setWorkDaysSettings((currentValue) =>
      currentValue.map((workDay) =>
        workDay.weekDay === weekDay
          ? {
              ...workDay,
              periods: workDay.periods.map((period) =>
                period.id === periodId ? { ...period, [field]: value } : period
              )
            }
          : workDay
      )
    );
  };

  const addWorkPeriod = (weekDay: WorkWeekDay) => {
    setWorkDaysSettings((currentValue) =>
      currentValue.map((workDay) =>
        workDay.weekDay === weekDay
          ? {
              ...workDay,
              periods: [
                ...workDay.periods,
                {
                  id: `${weekDay}-${Date.now()}-${workDay.periods.length + 1}`,
                  persistedId: null,
                  startTime: "08:00:00",
                  endTime: "12:00:00"
                }
              ]
            }
          : workDay
      )
    );
  };

  const removeWorkPeriod = (
    weekDay: WorkWeekDay,
    periodId: number | string
  ) => {
    setWorkDaysSettings((currentValue) =>
      currentValue.map((workDay) =>
        workDay.weekDay !== weekDay
          ? workDay
          : (() => {
              const periods = workDay.periods.filter(
                (period) => period.id !== periodId
              );

              return {
                ...workDay,
                enabled: periods.length > 0,
                periods
              };
            })()
      )
    );
  };

  const addSelectedDayOff = (item: DayOffItem) => {
    setSelectedDayOffs((currentValue) =>
      currentValue.some((selectedItem) => selectedItem.date === item.date)
        ? currentValue
        : [...currentValue, item]
    );
  };

  const handleAddCustomDayOff = () => {
    if (!dayOffDate || !dayOffDescription.trim()) {
      return;
    }

    const normalizedDescription = dayOffDescription.trim();
    addSelectedDayOff({
      id: `custom-${dayOffDate.toISOString()}-${normalizedDescription}`,
      persistedId: null,
      date: format(dayOffDate, "yyyy-MM-dd"),
      description: normalizedDescription,
      label: buildDayOffLabel(dayOffDate, normalizedDescription)
    });

    setDayOffDate(null);
    setDayOffDescription("");
  };

  const handleAddAllSuggestions = () => {
    NATIONAL_HOLIDAY_SUGGESTIONS.forEach((suggestion) =>
      addSelectedDayOff(buildSuggestionDayOffItem(suggestion))
    );
  };

  const removeSelectedDayOff = (id: number | string) => {
    setSelectedDayOffs((currentValue) =>
      currentValue.filter((item) => item.id !== id)
    );
  };

  const buildSuggestionDayOffItem = (
    suggestion: DayOffSuggestion
  ): DayOffItem => {
    const suggestionDate = buildSuggestionDate(suggestion);

    return {
      id: suggestion.id,
      persistedId: null,
      date: format(suggestionDate, "yyyy-MM-dd"),
      description: suggestion.description,
      label: buildDayOffLabel(suggestionDate, suggestion.description)
    };
  };

  const applySuggestionToForm = (suggestion: DayOffSuggestion) => {
    setDayOffDate(buildSuggestionDate(suggestion));
    setDayOffDescription(suggestion.description);
  };

  const buildSavePayload = (): SaveCalendarEditInput => ({
    id: calendarId,
    name: calendarName,
    confirmationMessage: confirmationMessage.trim(),
    breakAfterMinutes: meetingGapMinutes,
    botId: selectedBotId,
    userIds: Array.from(
      new Set(
        [ownerUserId, ...sharedUserIds].filter(
          (userId): userId is number => typeof userId === "number"
        )
      )
    ),
    workDays: workDaysSettings
      .filter((workDay) => workDay.enabled)
      .map((workDay) => ({
        id: workDay.id,
        calendarId,
        weekDay: workDay.weekDay,
        periods: workDay.periods.map((period) => ({
          id: period.persistedId,
          calendarWorkDayId: workDay.id,
          startTime: period.startTime,
          endTime: period.endTime
        }))
      })),
    dayOffs: selectedDayOffs.map((dayOff) => ({
      id: dayOff.persistedId,
      calendarId,
      date: dayOff.date,
      description: dayOff.description.trim() || null
    }))
  });

  const buildValidationFormValues = (): CalendarEditValidationForm => ({
    name: calendarName,
    confirmationMessage,
    breakAfterMinutes: meetingGapMinutes,
    workDays: workDaysSettings.map((workDay) => ({
      label: workDay.label,
      enabled: workDay.enabled,
      periods: workDay.periods.map((period) => ({
        startTime: period.startTime,
        endTime: period.endTime
      }))
    })),
    dayOffs: selectedDayOffs.map((dayOff) => ({
      date: dayOff.date,
      description: dayOff.description
    }))
  });

  const handleSave = async () => {
    if (!canSaveChanges) {
      return;
    }

    if (!confirmationMessage.trim()) {
      toast?.current?.show({
        life: 3000,
        severity: "warn",
        summary: "Aviso",
        detail: "Não é possível salvar com a mensagem de confirmação em branco."
      });
      return;
    }

    reset(buildValidationFormValues());

    const isValid = await trigger();

    if (!isValid) {
      toast?.current?.show({
        severity: "warn",
        content:
          getFirstErrorMessage(errors) ??
          "Revise os campos antes de salvar as configurações."
      });
      return;
    }

    try {
      setIsSaving(true);
      await saveCalendarEdit(buildSavePayload());
      await onSaved?.();
      toast?.current?.show({
        severity: "success",
        content: "Configurações salvas com sucesso."
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBotSelectionChange = (
    nextValue: number | "__none__" | null | undefined
  ) => {
    if (nextValue === "__none__") {
      if (selectedBotId !== null) {
        setShowRemoveBotModal(true);
        return;
      }

      setSelectedBotId(null);
      return;
    }

    if (nextValue == null) {
      setShowRemoveBotModal(true);
      return;
    }

    setSelectedBotId(nextValue);
  };

  if (isLoading || isSaving) {
    return (
      <FullscreenLoading>
        <div>
          <ProgressSpinner />
        </div>
      </FullscreenLoading>
    );
  }

  return (
    <SettingsContent>
      <EditCard style={canEditSettings ? undefined : readOnlyCardStyle}>
        <SettingsSection>
          <SettingsSectionHeader>
            <SettingsSectionIcon>
              <i className="pi pi-clock" />
            </SettingsSectionIcon>

            <SettingsSectionTitleGroup>
              <SettingsSectionTitle>
                Horário de Atendimento
              </SettingsSectionTitle>
              <SettingsSectionDescription>
                Defina os períodos de atendimento para cada dia, incluindo
                intervalos
              </SettingsSectionDescription>
            </SettingsSectionTitleGroup>
          </SettingsSectionHeader>

          {workDaysSettings.map((workDay) => (
            <WorkDayCard key={workDay.weekDay}>
              <WorkDayHeader>
                <WorkDayHeaderLeft>
                  <InputSwitch
                    checked={workDay.enabled}
                    onChange={(event) =>
                      toggleWorkDay(workDay.weekDay, Boolean(event.value))
                    }
                  />
                  <WorkDayTitle>{workDay.label}</WorkDayTitle>
                </WorkDayHeaderLeft>

                {!workDay.enabled ? (
                  <WorkDayUnavailable>Indisponível</WorkDayUnavailable>
                ) : null}
              </WorkDayHeader>

              {workDay.enabled ? (
                <WorkDayPeriods>
                  {workDay.periods.map((period, index) => (
                    <WorkPeriodRow key={period.id}>
                      <WorkPeriodLabel>{`Período ${
                        index + 1
                      }`}</WorkPeriodLabel>

                      <TimeDropdown
                        value={period.startTime}
                        options={timeOptions}
                        onChange={(event) =>
                          updateWorkPeriod(
                            workDay.weekDay,
                            period.id,
                            "startTime",
                            event.value
                          )
                        }
                      />

                      <WorkPeriodSeparator>às</WorkPeriodSeparator>

                      <TimeDropdown
                        value={period.endTime}
                        options={timeOptions}
                        onChange={(event) =>
                          updateWorkPeriod(
                            workDay.weekDay,
                            period.id,
                            "endTime",
                            event.value
                          )
                        }
                      />

                      <DeletePeriodButton
                        type="button"
                        onClick={() =>
                          removeWorkPeriod(workDay.weekDay, period.id)
                        }
                      >
                        <i
                          className="pi pi-times"
                          style={{ fontSize: "10px", color: "#667085" }}
                        />
                      </DeletePeriodButton>
                    </WorkPeriodRow>
                  ))}

                  <AddPeriodButton
                    type="button"
                    onClick={() => addWorkPeriod(workDay.weekDay)}
                  >
                    <i className="pi pi-plus" style={{ fontSize: "10px" }} />
                    <span>Adicionar período</span>
                  </AddPeriodButton>
                </WorkDayPeriods>
              ) : null}
            </WorkDayCard>
          ))}

          <SettingsHint>
            Dica: Use múltiplos períodos para definir horários com intervalo
            (ex: 08:00-12:00 e 14:00-18:00)
          </SettingsHint>
        </SettingsSection>
      </EditCard>

      <EditCard style={canEditSettings ? undefined : readOnlyCardStyle}>
        <SettingsSection>
          <SettingsSectionHeader>
            <DayOffSectionIcon>
              <i className="pi pi-calendar-times" />
            </DayOffSectionIcon>

            <SettingsSectionTitleGroup>
              <SettingsSectionTitle>Dias sem Atendimento</SettingsSectionTitle>
              <SettingsSectionDescription>
                Configure feriados, férias e dias pessoais sem atendimento
              </SettingsSectionDescription>
            </SettingsSectionTitleGroup>
          </SettingsSectionHeader>

          <DayOffFormCard>
            <DayOffDateField>
              <DayOffLabel>Data</DayOffLabel>
              <DayOffCalendarWrapper>
                <MyCalendar
                  value={dayOffDate}
                  onChange={(event) =>
                    setDayOffDate(event.value as Date | null)
                  }
                  dateFormat="dd/mm/yy"
                  placeholder="Selecionar data"
                  showIcon
                  style={{
                    border: "1px solid #ced4da",
                    borderRadius: "8px"
                  }}
                />
              </DayOffCalendarWrapper>
            </DayOffDateField>

            <DayOffDescriptionField>
              <DayOffLabel>Descrição</DayOffLabel>
              <DayOffInput
                value={dayOffDescription}
                onChange={(event) => {
                  setDayOffDescription(event.target.value);
                }}
                placeholder="Ex: Férias, Consulta médica..."
              />
            </DayOffDescriptionField>

            <DayOffAction>
              <DayOffAddButton
                type="button"
                color={primaryColor}
                disabled={!isDayOffFormComplete}
                onClick={handleAddCustomDayOff}
              >
                <i className="pi pi-plus" style={{ fontSize: "10px" }} />
                <span>Adicionar</span>
              </DayOffAddButton>
            </DayOffAction>
          </DayOffFormCard>

          {selectedDayOffs.length > 0 ? (
            <DayOffList>
              {selectedDayOffs.map((item) => (
                <DayOffChip key={item.id}>
                  <span>{item.label}</span>
                  <DayOffChipRemoveButton
                    type="button"
                    onClick={() => removeSelectedDayOff(item.id)}
                  >
                    <i className="pi pi-times" style={{ fontSize: "10px" }} />
                  </DayOffChipRemoveButton>
                </DayOffChip>
              ))}
            </DayOffList>
          ) : null}

          <DayOffSuggestionsHeader>
            <DayOffSuggestionsTitle>
              Sugestões: Feriados Nacionais
            </DayOffSuggestionsTitle>

            <DayOffSecondaryButton
              type="button"
              onClick={handleAddAllSuggestions}
            >
              Adicionar todos
            </DayOffSecondaryButton>
          </DayOffSuggestionsHeader>

          <DayOffList>
            {availableHolidaySuggestions.map((suggestion) => (
              <DayOffChip
                key={suggestion.id}
                style={{ cursor: "pointer" }}
                onClick={() => applySuggestionToForm(suggestion)}
              >
                <span>{`${String(suggestion.day).padStart(2, "0")}/${String(
                  suggestion.month
                ).padStart(2, "0")} - ${suggestion.description}`}</span>
              </DayOffChip>
            ))}
          </DayOffList>
        </SettingsSection>
      </EditCard>

      <EditCard style={canManageSharing ? undefined : readOnlyCardStyle}>
        <SettingsSection>
          <SettingsSectionHeader>
            <ShareAgendaSectionIcon>
              <i className="pi pi-users" />
            </ShareAgendaSectionIcon>

            <SettingsSectionTitleGroup>
              <SettingsSectionTitle>Compartilhar Agenda</SettingsSectionTitle>
              <SettingsSectionDescription>
                Permita que outros usuários visualizem ou editem sua agenda
              </SettingsSectionDescription>
            </SettingsSectionTitleGroup>
          </SettingsSectionHeader>

          <ShareAgendaField>
            <ShareAgendaLabel>Adicionar pessoas</ShareAgendaLabel>
            <ShareAgendaMultiSelect
              value={sharedUserIds}
              options={sharedUserOptions}
              optionLabel="name"
              optionValue="id"
              appendTo="self"
              onChange={(event) =>
                setSharedUserIds(normalizeSharedUserIds(event.value))
              }
              filter
              display="chip"
              showSelectAll={false}
              placeholder="Buscar por nome ou email..."
              selectedItemTemplate={(value: unknown) => {
                const option = getSharedUserOption(value);
                if (!option) return null;

                return <ShareAgendaChip>{option.name}</ShareAgendaChip>;
              }}
            />
          </ShareAgendaField>

          {sharedUserIds.length === 0 ? (
            <ShareAgendaEmptyState>
              <i className="pi pi-users" />
              <div>Sua agenda ainda não está compartilhada com ninguém</div>
            </ShareAgendaEmptyState>
          ) : null}
        </SettingsSection>
      </EditCard>

      <EditCard style={canEditSettings ? undefined : readOnlyCardStyle}>
        <SettingsSection>
          <SettingsSectionHeader>
            <MeetingGapSectionIcon>
              <i className="pi pi-clock" />
            </MeetingGapSectionIcon>

            <SettingsSectionTitleGroup>
              <SettingsSectionTitle>Tempo entre Reuniões</SettingsSectionTitle>
              <SettingsSectionDescription>
                Defina um intervalo mínimo entre os agendamentos
              </SettingsSectionDescription>
            </SettingsSectionTitleGroup>
          </SettingsSectionHeader>

          <MeetingGapField>
            <DayOffLabel>Intervalo entre reuniões</DayOffLabel>
            <MeetingGapDropdown
              value={meetingGapMinutes}
              options={MEETING_GAP_OPTIONS}
              optionLabel="label"
              optionValue="value"
              appendTo="self"
              placeholder="Escolha um intervalo"
              onChange={(event) => setMeetingGapMinutes(event.value)}
            />
            <MeetingGapHint>
              Este tempo será reservado automaticamente após cada reunião
            </MeetingGapHint>
          </MeetingGapField>
        </SettingsSection>
      </EditCard>

      <EditCard style={canEditSettings ? undefined : readOnlyCardStyle}>
        <SettingsSection>
          <SettingsSectionHeader>
            <ConfirmationSectionIcon>
              <i className="pi pi-comment" />
            </ConfirmationSectionIcon>

            <SettingsSectionTitleGroup>
              <SettingsSectionTitle>
                Mensagem de Confirmação
              </SettingsSectionTitle>
              <SettingsSectionDescription>
                Personalize a mensagem de confirmação do agendamento
              </SettingsSectionDescription>
            </SettingsSectionTitleGroup>
          </SettingsSectionHeader>

          <ConfirmationField>
            <DayOffLabel>Mensagem</DayOffLabel>
            <ConfirmationTextarea
              value={confirmationMessage}
              onChange={(event) => setConfirmationMessage(event.target.value)}
              placeholder="Ex: Olá, {{nome}}! Seu agendamento está confirmado para {{data}} às {{horario}}."
            />
          </ConfirmationField>

          <ConfirmationVariablesRow>
            <ConfirmationVariablesLabel>
              Variáveis disponíveis:
            </ConfirmationVariablesLabel>
            <ConfirmationVariableChip>{"{{nome}}"}</ConfirmationVariableChip>
            <ConfirmationVariableChip>{"{{data}}"}</ConfirmationVariableChip>
            <ConfirmationVariableChip>{"{{horario}}"}</ConfirmationVariableChip>
          </ConfirmationVariablesRow>

          <ConfirmationField>
            <DayOffLabel>Pré-visualização</DayOffLabel>
            <ConfirmationPreviewBox>
              {confirmationPreview}
            </ConfirmationPreviewBox>
          </ConfirmationField>

          <MeetingGapField>
            <DayOffLabel>
              Selecione um robô para o envio de confirmação
            </DayOffLabel>
            <MeetingGapDropdown
              value={selectedBotId ?? "__none__"}
              options={botDropdownOptions}
              optionLabel="label"
              optionValue="value"
              appendTo="self"
              placeholder="Envio chatbot"
              onChange={(event) => handleBotSelectionChange(event.value)}
            />
          </MeetingGapField>
        </SettingsSection>
      </EditCard>

      <Dialog
        visible={showRemoveBotModal}
        onHide={() => setShowRemoveBotModal(false)}
        draggable={false}
        header="Deseja remover o robô?"
        style={{ width: "575px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            Caso nenhum robô esteja selecionado, notificações de confirmação por
            telefone podem falhar no momento do envio. Deseja continuar?
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px"
            }}
          >
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={() => {
                setSelectedBotId(null);
                setShowRemoveBotModal(false);
              }}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={() => setShowRemoveBotModal(false)}
            />
          </div>
        </div>
      </Dialog>

      <SaveActions>
        <SaveButton
          type="button"
          color={primaryColor}
          disabled={isSaving || !canSaveChanges}
          onClick={handleSave}
        >
          Salvar
        </SaveButton>
      </SaveActions>
    </SettingsContent>
  );
};
